import React from 'react';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarLight } from '@fortawesome/pro-light-svg-icons';

export const Card = styled.div`
  background: ${COLORS.whiteBase};
  border: 1px solid #cfd9e4;
  border-radius: 12px;
  padding: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  box-shadow: 0 2px 8px rgba(0, 115, 163, 0.05);
`;

export const ErrorBanner = styled.div`
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  border: 1px solid ${COLORS.critical};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  margin-bottom: ${SPACERS.small};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const SuccessBanner = styled.div`
  background: ${COLORS.successBackground};
  color: ${COLORS.success};
  border: 1px solid ${COLORS.success};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  margin-bottom: ${SPACERS.small};
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${SPACERS.extraLarge} ${SPACERS.large};
  color: ${COLORS.grayDark};
  background: ${COLORS.greyLightest};
  border-radius: 12px;
  border: 1px dashed ${COLORS.greyBase};
  .icon {
    font-size: 40px;
    color: ${COLORS.grayBase};
    margin-bottom: ${SPACERS.small};
  }
`;

export const Pill = styled.span<{ tone?: 'primary' | 'neutral' | 'success' | 'warning' }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${p =>
    p.tone === 'success'
      ? COLORS.success
      : p.tone === 'warning'
      ? COLORS.warningDark
      : p.tone === 'primary'
      ? COLORS.ctaPrimary
      : COLORS.grayDarkest};
  background: ${p =>
    p.tone === 'success'
      ? COLORS.successBackground
      : p.tone === 'warning'
      ? COLORS.warningBackground
      : p.tone === 'primary'
      ? COLORS.ctaPrimaryBackground
      : COLORS.greyLight};
`;

export const CharCount = styled.span<{ over?: boolean }>`
  font-size: 12px;
  color: ${p => (p.over ? COLORS.critical : COLORS.grayDark)};
`;

const ScoreTrack = styled.div`
  width: 100px;
  height: 6px;
  border-radius: 999px;
  background: ${COLORS.greyLight};
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
  margin-right: ${SPACERS.tiny};
`;

const ScoreFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${p => p.pct}%;
  background: ${COLORS.ctaPrimary};
  border-radius: 999px;
`;

export const ScoreBar: React.FC<{ score: number }> = ({ score }) => {
  const pct = Math.max(0, Math.min(100, Math.round(score * 100)));
  return (
    <span title={`Score: ${score.toFixed(3)}`}>
      <ScoreTrack>
        <ScoreFill pct={pct} />
      </ScoreTrack>
      <span style={{ fontSize: 12, color: COLORS.grayDark }}>
        {score.toFixed(2)}
      </span>
    </span>
  );
};

const StarsWrapper = styled.span<{ interactive?: boolean }>`
  display: inline-flex;
  gap: 2px;
  color: #e6a817;
  svg {
    cursor: ${p => (p.interactive ? 'pointer' : 'default')};
  }
`;

export const RatingStars: React.FC<{
  value: number;
  onChange?: (value: number) => void;
  size?: number;
}> = ({ value, onChange, size = 16 }) => (
  <StarsWrapper interactive={!!onChange}>
    {[1, 2, 3, 4, 5].map(i => (
      <FontAwesomeIcon
        key={i}
        icon={i <= value ? faStarSolid : faStarLight}
        style={{ fontSize: size }}
        onClick={onChange ? () => onChange(i) : undefined}
      />
    ))}
  </StarsWrapper>
);

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleString();
};
